import { dateOnlyOf, nextDateOnlyDay } from '@/lib/utils/custom-date/custom-date';
import { hrTermOn } from '@/lib/utils/work-days/work-days';
import {
  DayDisplayStatus,
  dayDisplayStatusOf,
} from '../components/day-status-badge/day-status-badge';
import type { HrTermWorkDays } from './attendance.service';
import {
  attendanceDayOf,
  isAttendanceInProgress,
  scheduledWorkSecondsOn,
  toAttendanceDate,
} from './attendance-rules';

/** An approved or pending leave request, as the day list reads it. */
export interface AttendanceDayLeave {
  /** `Approved` or `Pending`. */
  Status?: string;
  Title?: string;
  LeaveCategory?: string;
  LeaveType?: string;
  /** Date only. */
  StartDate: string;
  /** Date only. */
  EndDate?: string;
}

/** A national holiday, as the day list reads it. */
export interface AttendanceDayHoliday {
  Name?: string;
  /** Date only. */
  StartDate: string;
}

/** The parts of an Attendance record the day list reads. */
type DayRecord = NonNullable<Parameters<typeof dayDisplayStatusOf>[0]> & {
  AttendanceDate: string;
};

/** One calendar day of the list: its record, if any, and what it is shown as. */
export interface AttendanceDayRow<R extends DayRecord> {
  /** "YYYY-MM-DD". */
  day: string;
  /** The day as the `AttendanceDate` that stores it (what the date column formats and sorts). */
  AttendanceDate: string;
  record: R | null;
  status: DayDisplayStatus | null;
  /** A holiday's or a leave's name, shown instead of the status's own label. */
  label?: string;
}

/** A day can be corrected once it is no longer being worked; a missed working day too. */
export const isCorrectableDay = <R extends DayRecord>(
  row: AttendanceDayRow<R>,
  now: Date = new Date()
): boolean => (row.record ? !isAttendanceInProgress(row.record, now) : row.status === 'Absent');

/** A half-day leave (every request, legacy ones included, carries its `LeaveType`). */
const isHalfDayLeave = (leave: AttendanceDayLeave) => leave.LeaveType === 'HalfDay';

const leaveName = (leave: AttendanceDayLeave) => leave.LeaveCategory || leave.Title || '';

/**
 * Every day from `from` to `to` ("YYYY-MM-DD", inclusive), newest first, like the legacy attendance
 * list (`GetUserAttendances`). A day shows, first match wins:
 *
 * 1. a record still clocked in: Working, or a missing clock-out once its shift is over;
 * 2. a holiday: its name;
 * 3. a full-day leave: the leave's name (legacy: the leave category), even when the day has a record;
 * 4. a record: where it was worked (WFH / WFO);
 * 5. a half-day leave: the leave's name;
 * 6. a weekday the HR term in force doesn't work: Weekend;
 * 7. a working day of a leave still waiting for approval: Leave (pending) — the leave sheet already
 *    charges it, so the day is not shown absent (a record or an approved leave of the day comes first);
 * 8. today or a later day: nothing yet;
 * 9. otherwise a missed working day: Absent.
 *
 * Without HR term work days no day is a weekend or absent (nothing is guessed). Pure: `today` is the
 * organization's day.
 */
export const buildAttendanceDays = <R extends DayRecord>({
  from,
  to,
  today,
  records,
  leaves,
  holidays,
  terms,
  now = new Date(),
}: {
  from: string;
  to: string;
  today: string;
  records: R[];
  leaves: AttendanceDayLeave[];
  holidays: AttendanceDayHoliday[];
  terms: HrTermWorkDays[];
  now?: Date;
}): AttendanceDayRow<R>[] => {
  if (!from || !to || from > to) return [];
  const recordByDay = new Map(
    records.map((record) => [attendanceDayOf(record.AttendanceDate), record])
  );
  const holidayByDay = new Map(holidays.map((holiday) => [dateOnlyOf(holiday.StartDate), holiday]));
  const leaveOn = (day: string, pending: boolean) =>
    leaves.find(
      (leave) =>
        (leave.Status === 'Pending') === pending &&
        dateOnlyOf(leave.StartDate) <= day &&
        day <= dateOnlyOf(leave.EndDate ?? leave.StartDate)
    );

  const rows: AttendanceDayRow<R>[] = [];
  for (let day = from; day <= to; day = nextDateOnlyDay(day)) {
    const record = recordByDay.get(day) ?? null;
    const row = (status: DayDisplayStatus | null, label?: string): AttendanceDayRow<R> => ({
      day,
      AttendanceDate: toAttendanceDate(day),
      record,
      status,
      ...(label ? { label } : {}),
    });

    const recordStatus = record ? dayDisplayStatusOf(record, now) : null;
    const holiday = holidayByDay.get(day);
    const leave = leaveOn(day, false);
    const workDays = hrTermOn(terms, day)?.WorkDays;

    if (recordStatus === 'Working' || recordStatus === 'MissingClockOut')
      rows.push(row(recordStatus));
    else if (holiday) rows.push(row('Holiday', holiday.Name));
    else if (leave && !isHalfDayLeave(leave)) rows.push(row('Leave', leaveName(leave)));
    else if (record) rows.push(row(recordStatus === 'NoRecord' ? null : recordStatus));
    else if (leave) rows.push(row('Leave', leaveName(leave)));
    else if (!workDays?.length) rows.push(row(null));
    else if (scheduledWorkSecondsOn(workDays, day) <= 0) rows.push(row('Weekend'));
    else if (leaveOn(day, true)) rows.push(row('LeavePending'));
    else if (day >= today) rows.push(row(null));
    else rows.push(row('Absent'));
  }
  return rows.reverse();
};
